import { Injectable } from '@angular/core';
import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SupabaseService {
  private supabase: SupabaseClient;

  constructor() {
    this.supabase = createClient(
      environment.supabaseUrl,
      environment.supabaseAnonKey
    );
  }

  get client(): SupabaseClient {
    return this.supabase;
  }

  async getSession(): Promise<Session | null> {
    const { data } = await this.supabase.auth.getSession();
    return data.session;
  }

  async updateUser(attributes: {
    email?: string;
    password?: string;
    data?: Record<string, unknown>;
  }) {
    return this.supabase.auth.updateUser(attributes);
  }

  /**
   * Reautentica o usuário atual validando a senha informada. Usado antes de
   * operações sensíveis (troca de e-mail/senha). Retorna true se a senha confere.
   */
  async verificarSenha(email: string, senha: string): Promise<boolean> {
    const { error } = await this.supabase.auth.signInWithPassword({ email, password: senha });
    return !error;
  }

  async cpfEmUso(cpf: string): Promise<boolean> {
    const cpfLimpo = (cpf ?? '').replace(/\D/g, '');
    if (!cpfLimpo) return false;

    const { data, error } = await this.supabase.rpc('cpf_em_uso', {
      p_cpf: cpfLimpo,
    });

    if (error) throw error;
    return data === true;
  }

  /**
   * Lookup por e-mail EXATO de um cliente (Party Owner), via RPC privilegiada
   * (SECURITY DEFINER). Ignora a RLS para permitir reaproveitar contas já
   * existentes mesmo que ainda não estejam vinculadas ao buffet.
   */
  async buscarClientePorEmail(email: string): Promise<{
    id_usuario: string;
    nome: string;
    email: string;
    status: string | null;
  } | null> {
    const emailLimpo = (email ?? '').trim().toLowerCase();
    if (!emailLimpo) return null;

    const { data, error } = await this.supabase.rpc('buscar_cliente_por_email', {
      p_email: emailLimpo,
    });

    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row ?? null;
  }

  /**
   * Dados (não sensíveis) do dono de um evento do buffet do company admin
   * logado, via RPC privilegiada. Retorna null se o evento não pertencer à
   * empresa do usuário autenticado.
   */
  async dadosDonoEvento(idEvento: string): Promise<{
    id_usuario: string;
    nome: string;
    email: string;
    cpf: string | null;
    status: string | null;
  } | null> {
    if (!idEvento) return null;

    const { data, error } = await this.supabase.rpc('dados_dono_evento', {
      p_id_evento: idEvento,
    });

    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row ?? null;
  }

  async signIn(email: string, password: string) {
    return this.supabase.auth.signInWithPassword({ email, password });
  }

  /**
   * Dispara o e-mail de recuperação de senha. O link do e-mail abre
   * /auth/redefinir-senha já com a sessão de recovery no hash da URL.
   */
  async resetPasswordForEmail(email: string) {
    return this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/redefinir-senha`,
    });
  }

  async signOut() {
    return this.supabase.auth.signOut();
  }
}
